'use client'

import type { StaticImageData } from 'next/image'

import { cn } from '@/utilities/ui'
import NextImage from 'next/image'
import React from 'react'

import type { Props as MediaProps } from '../types'

import { cssVariables } from '@/cssVariables'
import { getClientSideURL } from '@/utilities/getURL'

const { breakpoints } = cssVariables

// A base64 encoded image to use as a placeholder while the image is loading
const placeholderBlur =
  'data:image/png;base64,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'

export const ImageMedia: React.FC<MediaProps> = (props) => {
  const {
    alt: altFromProps,
    fill,
    imgClassName,
    priority,
    quality: qualityFromProps,
    fetchPriority,
    style,
    resource,
    size: sizeFromProps,
    sizes: sizesFromProps,
    src: srcFromProps,
    loading: loadingFromProps,
  } = props

  let width: number | undefined
  let height: number | undefined
  let alt = altFromProps
  let src: StaticImageData | string | null = srcFromProps || null

  if (!src && resource && typeof resource === 'object') {
    const { alt: altFromResource, height: fullHeight, url, width: fullWidth } = resource

    // Provide fallback dimensions for logos and other images missing width/height
    width = fullWidth || 200 // Fallback width for logos
    height = fullHeight || 80 // Fallback height for logos
    alt = altFromResource || ''

    const cacheTag = resource.updatedAt

    // Check if the URL is already absolute (starts with http:// or https://)
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      src = `${url}?${cacheTag}`
    } else {
      src = `${getClientSideURL()}${url}?${cacheTag}`
    }
  }

  // Handle case where we have src prop but no resource (fallback scenario)
  if (src && !width && !height) {
    width = 200 // Default width for images without dimensions
    height = 80 // Default height for images without dimensions
  }

  // Ensure width and height are always defined for Next.js Image
  width = width || 200
  height = height || 80

  // Optimize quality based on use case, priority, and image size
  const quality =
    qualityFromProps !== undefined
      ? qualityFromProps
      : priority
        ? 85 // Balanced quality for LCP critical images
        : width && height
          ? width > 1200
            ? 75 // Large images: aggressive compression for file size
            : width > 600
              ? 70 // Medium images: more compression for web display
              : 65 // Small images: maximum compression for file size
          : 70 // Default optimized quality

  // Advanced responsive sizes calculation for better performance - Fixed for Core Web Vitals
  const sizes = sizesFromProps
    ? sizesFromProps
    : sizeFromProps
      ? sizeFromProps
      : fill
        ? priority
          ? '100vw' // Full viewport for LCP critical images
          : '(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 100vw, 100vw'
        : (() => {
            // OPTIMIZED: Precise sizing based on actual display dimensions from Lighthouse
            // This prevents oversized image downloads and dramatically improves Core Web Vitals

            // Hero/Large images - typically displayed around 600-800px max
            if (width && height && width > 1200) {
              return '(max-width: 640px) 640px, (max-width: 768px) 720px, (max-width: 1024px) 800px, (max-width: 1280px) 900px, 1000px'
            }

            // Card/Grid images - typically displayed around 300-500px
            if (width && height && width > 600) {
              return '(max-width: 640px) 400px, (max-width: 768px) 350px, (max-width: 1024px) 400px, (max-width: 1280px) 450px, 500px'
            }

            // Small images/thumbnails - typically displayed around 150-300px
            if (width && height && width <= 600) {
              return '(max-width: 640px) 200px, (max-width: 768px) 180px, (max-width: 1024px) 160px, 150px'
            }

            // Fallback: Conservative sizing for unknown dimensions
            return '(max-width: 640px) 400px, (max-width: 768px) 350px, (max-width: 1024px) 400px, 450px'
          })()

  // Optimize fetch priority for critical images - prioritize fetchPriority and priority over loading
  const optimizedFetchPriority = fetchPriority || (priority ? 'high' : 'auto')

  // Optimize loading strategy - when priority is true, don't set loading prop to avoid conflicts
  // Priority images are automatically eager loaded by Next.js
  const optimizedLoading = priority ? undefined : loadingFromProps || 'lazy'

  // Debug logging for mobile image issues (only in development)
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    const isMobile = window.innerWidth <= 768
    if (isMobile && !src) {
      console.warn('Media Component: No src found for mobile device', { resource, width, height })
    }
    if (isMobile && width && width > 1200) {
      console.log('Media Component: Large image on mobile', { width, height, sizes })
    }
  }

  return (
    <picture>
      {src && (
        <NextImage
          alt={alt || ''}
          className={cn(imgClassName)}
          style={style}
          fill={fill}
          height={!fill ? height : undefined}
          placeholder="blur"
          blurDataURL={placeholderBlur}
          priority={priority}
          quality={quality}
          fetchPriority={optimizedFetchPriority}
          {...(optimizedLoading && { loading: optimizedLoading })}
          sizes={sizes}
          src={src}
          width={!fill ? width : undefined}
          // Ensure modern formats are preferred and optimization is enabled
          unoptimized={false} // Enable Next.js optimization
        />
      )}
    </picture>
  )
}
